<div class="space-y-3">
  @if (!wallets()) {
  <div class="py-10 bg-slate-50 border border-slate-200 rounded-xl text-center text-slate-400">
    <mat-icon class="text-3xl text-slate-300 block mb-1">account_balance_wallet</mat-icon>
    <p class="text-xs font-semibold text-slate-600">Credit could not be loaded</p>
  </div>
  } @else {

  @for (wallet of holderWallets(); track wallet.parkerId) {
  <div class="border border-slate-200 rounded-xl bg-white overflow-hidden">
    <!-- Holder header: balance + where it may be spent -->
    <div class="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-slate-100">
      <div class="min-w-0">
        <div class="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{{ holderName(wallet.parkerId) }}</div>
        <div class="text-2xl font-bold text-slate-900">{{ formatCents(wallet.balanceInCents, wallet.currency ?? 'EUR') }}</div>
        <div class="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
          <mat-icon class="text-sm">place</mat-icon>
          <span class="truncate">{{ allowedSpacesLabel(wallet) }}</span>
        </div>
      </div>
      @if (canEdit() && editingParkerId() !== wallet.parkerId) {
      <button type="button" (click)="startEdit(wallet)"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50">
        <mat-icon class="text-base">edit_location_alt</mat-icon>
        Allowed spaces
      </button>
      }
    </div>

    <!-- Allowed-spaces editor -->
    @if (editingParkerId() === wallet.parkerId) {
    <div class="p-4 bg-slate-50 border-b border-slate-100 space-y-3">
      <div class="flex items-center justify-between">
        <span class="text-xs font-semibold text-slate-700">All parking spaces</span>
        <label class="relative inline-flex items-center cursor-pointer">
          <input type="checkbox" [checked]="draftAllowAll()" (change)="draftAllowAll.set(!draftAllowAll())" class="sr-only peer">
          <div class="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
        </label>
      </div>

      @if (!draftAllowAll()) {
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
        @for (space of spaces(); track space.id) {
        <button type="button" (click)="toggleSpace(space.id!)"
          class="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-xs font-semibold text-left transition-colors"
          [class.border-blue-300]="draftSpaceIds().has(space.id!)" [class.bg-blue-50]="draftSpaceIds().has(space.id!)"
          [class.text-blue-700]="draftSpaceIds().has(space.id!)"
          [class.border-slate-200]="!draftSpaceIds().has(space.id!)" [class.bg-white]="!draftSpaceIds().has(space.id!)"
          [class.text-slate-700]="!draftSpaceIds().has(space.id!)">
          <span class="truncate">{{ space.name }}</span>
          @if (draftSpaceIds().has(space.id!)) {
          <mat-icon class="text-base">check_circle</mat-icon>
          }
        </button>
        }
      </div>
      }

      <div class="flex justify-end gap-2">
        <button type="button" (click)="cancelEdit()" [disabled]="saving()"
          class="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50">
          Cancel
        </button>
        <button type="button" (click)="save()" [disabled]="saving() || (!draftAllowAll() && draftSpaceIds().size === 0)"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
          @if (saving()) { <mat-spinner diameter="14"></mat-spinner> }
          Save
        </button>
      </div>
    </div>
    }

    <!-- Ledger -->
    @if ((wallet.entries ?? []).length === 0) {
    <div class="py-6 text-center text-[11px] text-slate-400">No credit movements yet.</div>
    } @else {
    <div class="overflow-x-auto">
      <table class="w-full text-left text-xs">
        <thead class="bg-slate-50 text-slate-500 uppercase tracking-wider border-b border-slate-200">
          <tr>
            <th class="py-2.5 px-3 font-semibold">When</th>
            <th class="py-2.5 px-3 font-semibold">Type</th>
            <th class="py-2.5 px-3 font-semibold">Where</th>
            <th class="py-2.5 px-3 font-semibold">Plate</th>
            <th class="py-2.5 px-3 font-semibold text-right">Amount</th>
            <th class="py-2.5 px-3 font-semibold text-right">Balance</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100 bg-white">
          @for (entry of wallet.entries; track entry.id) {
          <tr class="hover:bg-slate-50/80 transition-colors">
            <td class="py-2.5 px-3 text-slate-700">{{ entry.occurredAt | date:'dd.MM.yyyy HH:mm' }}</td>
            <td class="py-2.5 px-3 text-slate-700">{{ entryLabel(entry) }}</td>
            <td class="py-2.5 px-3 font-semibold text-slate-800">{{ entrySpace(entry) }}</td>
            <td class="py-2.5 px-3 text-slate-600">{{ entry.licensePlate ?? '—' }}</td>
            <td class="py-2.5 px-3 text-right font-semibold" [class.text-emerald-700]="(entry.amountInCents ?? 0) > 0"
              [class.text-slate-700]="(entry.amountInCents ?? 0) <= 0">
              {{ (entry.amountInCents ?? 0) > 0 ? '+' : '' }}{{ formatCents(entry.amountInCents) }}
            </td>
            <td class="py-2.5 px-3 text-right text-slate-600">{{ formatCents(entry.balanceAfterInCents) }}</td>
          </tr>
          }
        </tbody>
      </table>
    </div>
    }
  </div>
  } @empty {
  <div class="py-10 bg-slate-50 border border-slate-200 rounded-xl text-center text-slate-400">
    <mat-icon class="text-3xl text-slate-300 block mb-1">account_balance_wallet</mat-icon>
    <p class="text-xs font-semibold text-slate-600">No parker holds this plate</p>
  </div>
  }

  <!-- Credit that stayed with former owners when the plate changed hands -->
  @if (formerOwnerEntries().length > 0) {
  <div class="border border-amber-200 rounded-xl bg-amber-50/40 overflow-hidden">
    <div class="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-amber-700 border-b border-amber-200">
      Earned on this plate by former owners
    </div>
    <div class="overflow-x-auto">
      <table class="w-full text-left text-xs">
        <tbody class="divide-y divide-amber-100">
          @for (entry of formerOwnerEntries(); track entry.id) {
          <tr>
            <td class="py-2 px-3 text-slate-700">{{ entry.occurredAt | date:'dd.MM.yyyy HH:mm' }}</td>
            <td class="py-2 px-3 text-slate-700">{{ entryLabel(entry) }}</td>
            <td class="py-2 px-3 text-slate-800 font-semibold">{{ entrySpace(entry) }}</td>
            <td class="py-2 px-3 text-right font-semibold text-slate-700">{{ formatCents(entry.amountInCents) }}</td>
          </tr>
          }
        </tbody>
      </table>
    </div>
  </div>
  }
  }
</div>
